import React, { useState, useEffect, useRef } from 'react';
import { sessionApi } from '../services/api';
import './TitleEditor.css';

interface TitleEditorProps {
  title: string;
  sessionId: string;
  onTitleUpdate: (newTitle: string) => void;
}

const TitleEditor: React.FC<TitleEditorProps> = ({ title, sessionId, onTitleUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setIsEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditValue(title);
    setError(null);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    
    if (!trimmedValue || trimmedValue === title) {
      cancelEditing();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await sessionApi.update(sessionId, { title: trimmedValue });
      onTitleUpdate(trimmedValue);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save title');
      console.error('Error saving title:', err);
    } finally {
      setIsSaving(false);
    }
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 40) {
      setEditValue(value);
    }
  };

  return (
    <div className="title-editor">
      {!isEditing ? (
        <div className="title-view" onClick={startEditing}>
          <h2 className="session-title">{title}</h2>
          <button 
            className="edit-button" 
            onClick={(e) => {
              e.stopPropagation();
              startEditing();
            }}
            title="Edit title"
            disabled={isSaving}
          >
            {isSaving ? (
              <span className="spinner" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
              </svg>
            )}
          </button>
        </div>
      ) : (
        <div className="title-edit">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleChange}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="title-input"
            placeholder="Enter a title..."
            maxLength={40}
          />
          <span className="char-count">{editValue.length}/40</span>
          {error && <span className="error-message">{error}</span>}
        </div>
      )}
    </div>
  );
};

export default TitleEditor;